import React from 'react'
import {
	Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Grid, Divider
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'


function KpiCard({ value, label, icon, color }) {
	return <Card sx={{ flex: 1 }}>
		<CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
			<Box sx={{
				width: 48, height: 48, borderRadius: '50%',
				backgroundColor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
			}}>
				<IconSeeker icon={icon} sx={{ fontSize: 20, color }} />
			</Box>
			<Box>
				<Typography variant="h5" sx={{ fontWeight: 700 }}>{value.toLocaleString()}</Typography>
				<Typography variant="caption" sx={{ color: 'secondary.light' }}>{label}</Typography>
			</Box>
		</CardContent>
	</Card>
}

function PieChartPlaceholder({ data, colors }) {
	const total = data.reduce((s, d) => s + d.value, 0)
	return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2 }}>
		<Box sx={{
			width: 120, height: 120, borderRadius: '50%', position: 'relative',
			background: `conic-gradient(${data.map((d, i) => {
				const start = data.slice(0, i).reduce((s, x) => s + x.value, 0) / total * 360
				const end = start + (d.value / total * 360)
				return `${colors[i % colors.length]} ${start}deg ${end}deg`
			}).join(', ')})`,
		}} />
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
			{data.map((d, i) => (
				<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors[i % colors.length] }} />
					<Typography sx={{ fontSize: '0.72rem' }}>{d.label}: <strong>{d.value.toLocaleString()}</strong></Typography>
				</Box>
			))}
		</Box>
	</Box>
}

function BarChartPlaceholder() {
	const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
	const current = [185, 210, 195, 230, 245, 120, 95]
	const previous = [160, 180, 175, 200, 220, 110, 85]
	const max = Math.max(...current, ...previous)

	return <Card>
		<CardContent>
			<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
				Daily searches vs Previous Month
			</Typography>
			<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 160, px: 1 }}>
				{days.map((day, i) => (
					<Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
						<Box sx={{ display: 'flex', gap: 0.3, alignItems: 'flex-end', width: '100%', justifyContent: 'center' }}>
							<Box sx={{
								width: '40%', height: (current[i] / max) * 130,
								bgcolor: 'primary.main', borderRadius: '3px 3px 0 0',
							}} />
							<Box sx={{
								width: '40%', height: (previous[i] / max) * 130,
								bgcolor: 'secondary.lighter', borderRadius: '3px 3px 0 0',
							}} />
						</Box>
						<Typography sx={{ fontSize: '0.65rem', color: 'secondary.light' }}>{day}</Typography>
					</Box>
				))}
			</Box>
			<Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1.5 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<Box sx={{ width: 12, height: 12, bgcolor: 'primary.main', borderRadius: 1 }} />
					<Typography sx={{ fontSize: '0.72rem' }}>Current period</Typography>
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<Box sx={{ width: 12, height: 12, bgcolor: 'secondary.lighter', borderRadius: 1 }} />
					<Typography sx={{ fontSize: '0.72rem' }}>Previous month</Typography>
				</Box>
			</Box>
		</CardContent>
	</Card>
}

function InfoBlockDemo({ title, icon, data, colors }) {
	return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
		<Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
		<PieChartPlaceholder data={data} colors={colors} />
		<Divider sx={{ width: '100%' }} />
		<Table size="small">
			<TableHead>
				<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.7rem' } }}>
					<TableCell>{title.replace('Searches by ', '')}</TableCell>
					<TableCell align="center">Searches</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{data.map((d, i) => (
					<TableRow key={i} sx={{ '& td': { fontSize: '0.75rem', py: 1 } }}>
						<TableCell>{d.label}</TableCell>
						<TableCell align="center" sx={{ fontWeight: 600 }}>{d.value.toLocaleString()}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	</Box>
}


const COMPANY_DATA = [
	{ label: 'DrivePro Iberia', value: 487 },
	{ label: 'DrivePro UK', value: 312 },
	{ label: 'DrivePro France', value: 245 },
	{ label: 'DrivePro Germany', value: 189 },
	{ label: 'DrivePro Italy', value: 147 },
]

const USER_DATA = [
	{ label: 'john.smith@drivepro.demo', value: 342 },
	{ label: 'maria.garcia@drivepro.demo', value: 289 },
	{ label: 'pierre.dupont@drivepro.demo', value: 234 },
	{ label: 'anna.mueller@drivepro.demo', value: 178 },
	{ label: 'luca.rossi@drivepro.demo', value: 145 },
]

const SITE_DATA = [
	{ label: 'rentalspot.demo', value: 410 },
	{ label: 'pricefly.demo', value: 298 },
	{ label: 'eurodrive.demo', value: 256 },
	{ label: 'bookride.demo', value: 187 },
	{ label: 'autolink.demo', value: 129 },
]

const PIE_COLORS_1 = ['#1976d2', '#42a5f5', '#90caf9', '#bbdefb', '#e3f2fd']
const PIE_COLORS_2 = ['#2e7d32', '#4caf50', '#81c784', '#a5d6a7', '#c8e6c9']
const PIE_COLORS_3 = ['#e65100', '#ff9800', '#ffb74d', '#ffe0b2', '#fff3e0']


export default function AnalyticsDemoData() {
	return <Grid container spacing={2}>
		{/* KPI Cards */}
		<Grid item xs={12}>
			<Box sx={{ display: 'flex', gap: 2 }}>
				<KpiCard value={1380} label="Date Range" icon="calendar-range" color="#2e7d32" />
				<KpiCard value={312} label="Week" icon="calendar-week" color="#1976d2" />
				<KpiCard value={1890} label="Month" icon="calendar" color="#e65100" />
			</Box>
		</Grid>

		{/* Bar Chart */}
		<Grid item xs={12}>
			<BarChartPlaceholder />
		</Grid>

		{/* 3 Info Blocks */}
		<Grid item xs={4}>
			<InfoBlockDemo title="Searches by Company" icon="building" data={COMPANY_DATA} colors={PIE_COLORS_1} />
		</Grid>
		<Grid item xs={4}>
			<InfoBlockDemo title="Searches by User" icon="user" data={USER_DATA} colors={PIE_COLORS_2} />
		</Grid>
		<Grid item xs={4}>
			<InfoBlockDemo title="Searches by Site" icon="globe-pointer" data={SITE_DATA} colors={PIE_COLORS_3} />
		</Grid>
	</Grid>
}
